import type { HoursSchedule, InventoryResponse, MenuOption, ProfileField } from "@starsach/shared-types";
import { TOOLTIPS } from "../../lib/tooltips";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const REFERENCE_TYPES = ["queue", "flow", "prompt", "hours_of_operation", "lex_bot"] as const;

const input = "rounded border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900";

/**
 * A menu is an array of objects because the Lambda block's `JSON`
 * response validation allows one (section 10.3) - so it is edited as a
 * list, and each option's target is picked from the inventory rather than
 * typed, which is what makes a typo a publish-time error instead of a
 * silent null on a live call.
 */
export function MenuEditor({
  field,
  inventory,
  onChange,
}: {
  field: Extract<ProfileField, { type: "menu" }>;
  inventory: InventoryResponse | undefined;
  onChange: (next: MenuOption[]) => void;
}) {
  const options = field.value;
  const update = (i: number, next: MenuOption) => onChange(options.map((o, j) => (j === i ? next : o)));

  return (
    <div className="mt-1 space-y-2">
      <ul className="space-y-2">
        {options.map((o, i) => (
          <li key={i} className="flex flex-wrap items-end gap-2 rounded border border-neutral-200 p-2 dark:border-neutral-800">
            <label className="text-xs">
              Key
              <input
                className={`${input} mt-1 block w-14`}
                value={o.dtmf}
                maxLength={1}
                aria-label={`Option ${i + 1} key`}
                onChange={(e) => update(i, { ...o, dtmf: e.target.value })}
              />
            </label>
            <label className="text-xs flex-1">
              Label
              <input className={`${input} mt-1 block w-full`} value={o.label} aria-label={`Option ${i + 1} label`} onChange={(e) => update(i, { ...o, label: e.target.value })} />
            </label>
            <label className="text-xs">
              Goes to
              <select
                className={`${input} mt-1 block`}
                aria-label={`Option ${i + 1} target type`}
                value={o.target.type}
                onChange={(e) => update(i, { ...o, target: { ...o.target, type: e.target.value as MenuOption["target"]["type"] } })}
              >
                {REFERENCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label className="text-xs flex-1">
              Named
              <select
                className={`${input} mt-1 block w-full`}
                aria-label={`Option ${i + 1} target`}
                value={o.target.name}
                onChange={(e) => update(i, { ...o, target: { ...o.target, name: e.target.value } })}
              >
                {(inventory?.items ?? []).filter((it) => it.type === o.target.type).map((it) => (
                  <option key={it.name} value={it.name}>{it.name}</option>
                ))}
                {!(inventory?.items ?? []).some((it) => it.type === o.target.type && it.name === o.target.name) && (
                  <option value={o.target.name}>{o.target.name} (not in this instance)</option>
                )}
              </select>
            </label>
            <button type="button" className="text-sm text-red-700 underline dark:text-red-400" onClick={() => onChange(options.filter((_, j) => j !== i))}>
              Remove
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="rounded border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700"
        disabled={options.length >= 12}
        onClick={() => onChange([...options, { dtmf: String(options.length + 1), label: "", target: { type: "queue", name: "" } }])}
      >
        Add an option
      </button>
    </div>
  );
}

/**
 * Hours are authored as a schedule and never returned as one: the lander
 * evaluates them in the customer's account at call time and tells the
 * flow open or closed (section 10.3). What is edited here is what it
 * evaluates.
 */
/** `windowsOnly`: the Hours tab's list view, where the card already owns the time zone and the closed announcement. Holidays are their own tab. */
export function HoursEditor({ field, onChange, windowsOnly = false, announcements = [] }: { field: Extract<ProfileField, { type: "hours" }>; onChange: (next: HoursSchedule) => void; windowsOnly?: boolean; announcements?: string[] }) {
  const v = field.value;

  return (
    <div className="mt-1 space-y-3 rounded border border-neutral-200 p-3 dark:border-neutral-800">
      {!windowsOnly && (
      <label className="block text-xs">
        Timezone
        <input
          className={`${input} mt-1 block w-64`}
          value={v.timezone}
          aria-label="Timezone"
          onChange={(e) => onChange({ ...v, timezone: e.target.value })}
        />
        <span className="mt-1 block text-neutral-500">
          An IANA zone such as Europe/London. Evaluated in your own account at call time, so an expiry lands on schedule
          even when a sync is late.
        </span>
      </label>
      )}

      <fieldset>
        <legend className="text-xs font-medium">Open hours</legend>
        <ul className="mt-1 space-y-1">
          {v.weekly.map((w, i) => (
            <li key={i} className="flex flex-wrap items-end gap-2">
              <label className="text-xs">
                Day
                <select
                  className={`${input} mt-1 block`}
                  aria-label={`Window ${i + 1} day`}
                  value={w.day}
                  onChange={(e) => onChange({ ...v, weekly: v.weekly.map((x, j) => (j === i ? { ...x, day: Number(e.target.value) } : x)) })}
                >
                  {DAYS.map((d, n) => <option key={d} value={n}>{d}</option>)}
                </select>
              </label>
              <label className="text-xs">
                Opens
                <input type="time" className={`${input} mt-1 block`} value={w.opens} aria-label={`Window ${i + 1} opens`}
                  onChange={(e) => onChange({ ...v, weekly: v.weekly.map((x, j) => (j === i ? { ...x, opens: e.target.value } : x)) })} />
              </label>
              <label className="text-xs">
                Closes
                <input type="time" className={`${input} mt-1 block`} value={w.closes} aria-label={`Window ${i + 1} closes`}
                  onChange={(e) => onChange({ ...v, weekly: v.weekly.map((x, j) => (j === i ? { ...x, closes: e.target.value } : x)) })} />
              </label>
              <button type="button" className="text-sm text-red-700 underline dark:text-red-400" onClick={() => onChange({ ...v, weekly: v.weekly.filter((_, j) => j !== i) })}>
                Remove
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="mt-1 rounded border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700"
          onClick={() => onChange({ ...v, weekly: [...v.weekly, { day: 1, opens: "09:00", closes: "17:00" }] })}
        >
          Add a window
        </button>
        <p className="mt-1 text-xs text-neutral-500">A window whose close is before its open runs past midnight.</p>
      </fieldset>

      {!windowsOnly && (
      <span className="block text-xs">
        Say when closed
        <AnnouncementRef value={v.closed_announcement ?? ""} announcements={announcements} label="Closed announcement" onChange={(closed_announcement) => onChange({ ...v, ...(closed_announcement ? { closed_announcement } : { closed_announcement: undefined }) })} />
      </span>
      )}
    </div>
  );
}

/**
 * "Say when closed" and a holiday's announcement name one of the profile's
 * announcements (the owner's ask of 2026-09-18): the lander resolves the
 * name to what the caller hears, so the flow plays it without a lookup.
 * Where the profile has no announcements yet, or an older value is free
 * text, the text is kept as it is.
 */
export function AnnouncementRef({ value, announcements, label, onChange }: { value: string; announcements: string[]; label: string; onChange: (next: string) => void }) {
  const known = announcements.includes(value);
  if (value && !known) {
    // Text authored before announcements had names: kept as typed, and it still plays as typed.
    return <input className={`${input} mt-1 block w-full`} value={value} aria-label={label} placeholder="Name of an announcement" onChange={(e) => onChange(e.target.value)} />;
  }
  if (announcements.length === 0) {
    // Never a text box: an announcement is authored on its own tab and chosen here by name (the first walk's finding).
    return (
      <select className={`${input} mt-1 block w-full`} value="" aria-label={label} disabled title={TOOLTIPS["flow.announcement.none-yet"]}>
        <option value="">No announcements yet - add one on the Announcements tab</option>
      </select>
    );
  }
  return (
    <select className={`${input} mt-1 block w-full`} value={value} aria-label={label} onChange={(e) => onChange(e.target.value)}>
      <option value="">Nothing - silence, then the flow decides</option>
      {announcements.map((a) => <option key={a} value={a}>{a}</option>)}
    </select>
  );
}
